// ORE VEINS — what a world is actually made of, below the surface.
//
// Terrain used to yield exactly two crust materials at every depth, on every world. The composition panel has always
// SAID otherwise — compositionFor picks a core, an outer core and a mantle, each from its own density band — but
// nothing ever mined them, so the whole metal half of the tech tree had no source and 27 of 64 real recipes were
// permanently unreachable. This module is what makes depth mean something: each interior layer hosts its own
// occasional ore pockets, and which ores those are is a function of the world's seed and the layer's depth.
//
// PURE AND SHARED, byte-identical with functions/lib/ore-veins.mjs, for the same reason transient-shape.js is: the
// server's kubeNameAt decides what a break CREDITS, so if the two sides disagreed about a vein you would mine copper
// and be paid regolith, or have the edit rejected outright. Every value here is derived from (addr, R, cell) with no
// mutable input anywhere.
// makeRng is INLINED rather than imported so this file can be copied byte-for-byte to functions/lib/ore-veins.mjs —
// an import path is the one thing that cannot survive that copy. genDensity.mjs carries its own copy for the same
// reason. FNV-1a seed into an xorshift32 stream; must stay identical to system-engine.js and transient-shape.js.
function makeRng(str) {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < str.length; i++) h = Math.imul(h ^ str.charCodeAt(i), 16777619);
    let a = h >>> 0;
    return () => { a = (a ^ (a << 13)) >>> 0; a = (a ^ (a >>> 17)) >>> 0; a = (a ^ (a << 5)) >>> 0; return a / 4294967296; };
}

// Depth is the value ladder, and it is expressed in DENSITY because the game already prices mining that way:
// mineTimeForDensity charges more for denser kubes, so deeper ore costs more to cut without a single extra rule.
// The bands also explain the bug this module fixes — the mineable crust band is 3.00-3.99, the ores sit at 2.85/2.95
// just below it and every structural metal at 4.10-5.40 just above, so nothing the tree needed was ever reachable.
// RATE IS PER COARSE BLOCK, and the layers differ in volume by more than fifty to one — on a R=44 world the mantle
// is ~392k cells and the core ~6.9k. A flat rate therefore starves exactly the layers that matter most: the first
// tuning pass produced 5,087 mantle veins and ZERO core, which would have left Cor — the endgame material — absent
// from most worlds. Deep rates are raised to compensate for the smaller volume, not because deep ore is common.
export const VEIN_BANDS = {
    crust:     { lo: 2.60, hi: 3.30, rate: 0.27, rMin: 1, rMax: 2 },   // the shallow bootstrap: Silica, Bauxite, Titanium Ore, Carbon, Silicon, Phosphorus, Halite
    subcrust:  { lo: 4.00, hi: 4.44, rate: 0.15, rMin: 1, rMax: 2 },   // light metals: Calcium, Magnesium, Potassium
    mantle:    { lo: 4.45, hi: 4.72, rate: 0.10, rMin: 1, rMax: 3 },   // Manganese, Chromium, Zirconium, Pyrite, Magnetite
    outercore: { lo: 4.73, hi: 4.99, rate: 0.30, rMin: 2, rMax: 3 },   // Gold, Copper, Cobalt, Silver
    core:      { lo: 5.00, hi: 9.99, rate: 0.55, rMin: 2, rMax: 3 },   // Cor, and anything heavier
};
// HOW MUCH OF THE CRUST IS ACTUALLY ORE-ELIGIBLE, now that a vein may not outcrop. MEASURED, not modelled: sampling
// three worlds at each of six sizes, the buried share of solid crust cells is
//
//     R      12     16     24     32     40     48
//     frac  .586   .595   .593   .591   .604   .611
//
// — flat, because the crust band is three cells thick at every size, so the surface-to-volume ratio that would make
// this scale with R cancels out. A closed-form 24·pmr²/cells model was tried first and drifted from .600 to .652
// across the same range; the constant is both simpler and closer.
//
// IT HAS TO BE COMPENSATED FOR, not just accepted. Without this the rule quietly broke the floor the whole module
// exists to guarantee — the smallest claimable world dropped to 8 Carbon against the 14 an Induction Smelter costs,
// which is the "a claim you could not finish the game on" failure, back again by a side door. Two places use it,
// for two different reasons: the crust's RATE rises by 1/frac so a world keeps about the ore it always had, and its
// effective block count falls by frac so the per-ore coverage floor still sees the volume it is really working with.
export const CRUST_BURIED = 0.59;
export const LAYER_ORDER = ['crust', 'subcrust', 'mantle', 'outercore', 'core'];

// A rare world is unusually generous: more pockets, bigger ones. Worth finding, worth claiming, worth trading with.
export const RICH_WORLD_CHANCE = 0.12, RICH_RATE_MULT = 1.9, RICH_RADIUS_BONUS = 1;

// Pockets live inside a coarse block and never cross it, so testing a cell is ONE hash rather than 27. That is the
// whole reason for the constraint: this runs per cell over a whole world on both the client and the server.
export const VEIN_BLOCK = 8;         // the coarse block on a full-size world
// ...but it has to SHRINK with the world. On a R=12 body the mantle spans about two blocks of 8, which cannot host
// enough pockets to cover six ores however high the rate goes: two small worlds generated no Iron at all, and Iron
// is the metal the first machine is made of. A smaller block on a smaller world gives the thin layers somewhere to
// put their veins. Derived from R alone, so it stays a pure function of the world.
export function veinBlockFor(R) { return Math.max(3, Math.min(VEIN_BLOCK, Math.floor((R || 0) / 4))); }
// A pocket must fit wholly inside its block — that is what lets one block hash answer for every cell in it — so the
// radius is capped here rather than clamped later, where an oversized one would silently spill and be missed.
export function maxRadiusFor(block) { return Math.max(1, Math.floor((block - 1) / 2)); }
export const MIN_POCKETS = 4;        // absolute floor per layer, per world
export const POCKETS_PER_ORE = 8;    // ...and at least this many per ore the layer can offer, so none is missed
// Five rather than two because presence is not enough — the supply has to cover the FIRST MACHINE. On a minimum-size
// world the mantle is thin and its pockets are capped at radius 1, so two pockets per ore yielded 5-16 Iron against
// the 18 an Induction Smelter costs: present, but still a claim you could not finish the game on. This floor only
// binds where a layer is too small to hit its nominal rate, so it changes small worlds and leaves large ones alone.

// Roughly how many coarse blocks a layer spans, from the band geometry alone. Used only to lift the rate on layers
// too small to hit their nominal one, so this needs to be proportionate, not exact.
export function layerBlocks(bands, layer) {
    const shell = (r) => Math.pow(2 * r + 1, 3);
    const vol = layer === 'core' ? shell(bands.coreR)
        : layer === 'outercore' ? shell(bands.ocR) - shell(bands.coreR)
        : layer === 'mantle' ? shell(bands.mantleR) - shell(bands.ocR)
        : layer === 'subcrust' ? shell(bands.surfInner) - shell(bands.mantleR)
        : (shell(bands.pmr) - shell(bands.surfInner)) * CRUST_BURIED;   // …of which only the buried share can host ore
    const blk = veinBlockFor(bands.pmr + 1);
    return Math.max(1, vol / (blk * blk * blk));
}

// The rate this layer actually uses. A flat per-block rate is a share of VOLUME, and the layers differ by more than
// fifty to one, so on a small world the deep layers round to nothing: a R=12 body — the minimum claimable size —
// generated three ores total, no Gold and no Cor, which is a claim you cannot finish the game on. Lifting the rate
// until a layer expects MIN_POCKETS keeps small worlds poor, which is fair, without making them dead ends.
export function layerRate(bands, layer, oreCount) {
    const band = VEIN_BANDS[layer];
    if (!band) return 0;
    // The floor has to scale with the POOL, not just the layer. A flat four pockets was fine when a band held two or
    // three ores, but the crust now offers seven — Silica, Carbon, Halite, Phosphorus, Silicon and both ores — and
    // four weighted draws simply miss most of them: a R=12 world generated 302 vein kubes and not one Silica, Carbon
    // or Iron, which are precisely the three the first machine is made of. Absence is a dead end, exactly as it was
    // when a quarter of worlds had no Gold, so the target rises with the number of ores the layer can offer.
    const want = Math.max(MIN_POCKETS, (oreCount || 1) * POCKETS_PER_ORE);
    return Math.min(0.9, Math.max(band.rate, want / layerBlocks(bands, layer)));
}

// Layer boundaries, ported verbatim from compositionFor in system-view.js so the panel and the ground agree.
export function layerBands(R) {
    const Rc = Math.max(12, Math.min(48, R));
    const pmr = Rc - 1;
    const coreR = Math.max(1, Math.round(Rc / 4) - 2);
    const ocR = coreR + Math.ceil(Rc / 8);
    const mantleR = Math.max(ocR + 1, Rc - 7);
    // FOUR VOXELS, NOT THREE, and the extra one is the price of ore no longer outcropping. A 3-voxel crust has only
    // its middle shell genuinely buried once the outer face is excluded, and that is not enough ground for seven ores
    // to hide in: on a minimum-size world it capped Carbon at 13 against the 14 an Induction Smelter costs, and no
    // rate could lift it — the pockets saturate long before the buried volume does. A fourth shell roughly doubles
    // the volume a vein may occupy without moving where the SURFACE is, which is what the player actually sees.
    const surfInner = Math.max(mantleR + 1, pmr - 4);
    return { coreR, ocR, mantleR, surfInner, pmr };
}

// Which layer a cell sits in. L-infinity radius with the same +0.5 / round convention isSolid uses, so a cell is
// never in one layer for generation and another for display.
export function layerAt(bands, x, y, z) {
    const r = Math.round(Math.max(Math.abs(x + 0.5), Math.abs(y + 0.5), Math.abs(z + 0.5)));
    if (r <= bands.coreR) return 'core';
    if (r <= bands.ocR) return 'outercore';
    if (r <= bands.mantleR) return 'mantle';
    if (r <= bands.surfInner) return 'subcrust';
    return 'crust';
}

// WHAT MUST NEVER BE VEINED — derived from the recipe graph, not listed by hand.
//
// The obvious rule is "anything a real recipe produces": Bauxite and Titanium Ore are veined because nothing makes
// them, while Aluminum and Titanium are not, because smelting is how you are meant to get them. That rule is right
// almost everywhere and wrong in exactly one place, which QA found the hard way: IRON is produced by smelt-pyrite at
// the Induction Smelter, and building an Induction Smelter takes Steel, which is made from Iron. Excluding it means you need a
// smelter to make the iron for the smelter. A whole maximum-size world only scatters 40 ferrous rocks — 5 Iron — so
// that lock cannot be picked by grinding either.
//
// So the rule gains one clause, stated as a property rather than a name: a material is exempt from exclusion if it
// is needed to BUILD the very station that would otherwise be its only source. That is computed here from the
// recipes themselves, so a future station whose construction needs its own output is covered automatically.
// FROZEN ON OCT 1, 2026 — the ores are part of the world, and the world must not move. This list was derived from the
// recipe graph (deriveVeinExclusions below), so every recipe change would have silently changed which ores lie in the
// ground of every existing world, claimed ones included: slot crafting made Steel smelter-only and the smelter a
// stone furnace, and the rule would then have pulled Iron out of the veins. So the set every world generator to date
// (versions 1-5) uses is written out here, exactly as derived from the recipes of Oct 1. A future generator version
// may derive a new one; until then recipes and ore veins are independent.
export const VEIN_EXCLUSIONS_OCT1 = Object.freeze([
    "Aerogel", "Aluminum", "Ammonia Gas", "Argon Gas", "Arid Glass", "Aura Glass", "Battery", "Bloom Glass",
    "Brass Alloy", "Bucket", "Chemical Synthesizer", "Cinder Glass", "Circuit Board", "Corona Glass", "Drift Glass", "Echo Glass",
    "Electrolyzer", "Ether Glass", "Farout Glass", "Fathom Glass", "Flash Glass", "Gleam Glass", "Glint Glass", "Gloam Glass",
    "Graphene Kube", "Halo Glass", "Haze Glass", "Heavy Water", "Hydrocarbon Ice", "Hydrogen", "Hyper-Cor Matrix", "Ignition Glass",
    "Inconel Alloy", "Induction Smelter", "Liquid Argon", "Liquid Chlorine", "Lull Glass", "Lumina Glass", "Methane", "Nitrogen",
    "Nova Glass", "Oxygen", "Photovoltaic Cell", "Power Core", "Quantum Assembler", "Radiance Glass", "Reach Glass", "Reinforced Glass",
    "Scorched Glass", "Silicon Carbide", "Slag", "Solar Panel", "Solis Glass", "Spirit Glass", "Starlight Glass", "Steel",
    "Structural Alloy", "Sulfur", "Sulfuric Acid", "Superconductor Kube", "Surge Glass", "Tide Glass", "Titan Plate", "Titanium",
    "Toolbench", "Toolbench (Aluminum)", "Toolbench (Bloom Wood)", "Toolbench (Copper)", "Toolbench (Drift Wood)", "Toolbench (Echo Wood)", "Toolbench (Fathom Wood)", "Toolbench (Glint Wood)",
    "Toolbench (Gloam Wood)", "Toolbench (Halo Wood)", "Toolbench (Iron)", "Toolbench (Scorched Wood)", "Toolbench (Solis Wood)", "Toolbench (Steel)", "Toolbench (Tide Wood)", "Toolbench (Titanium)",
    "Toolbench (Verge Wood)", "Toolbench (Void Wood)", "Veil Glass", "Verge Glass", "Void Glass", "Water", "Wisp Glass", "Xenon Gas"
]);
export function veinExclusions() {
    return new Set(VEIN_EXCLUSIONS_OCT1);
}

// How the frozen list was derived — kept for the next generator version, and so a test can show what the CURRENT
// recipes would derive (and that the frozen list, not that, is what worlds use).
export function deriveVeinExclusions(recipes, stationKubeFor) {
    const real = (recipes || []).filter((r) => r && !/^(merge|split)-/.test(String(r.id || '')));
    const outs = (r) => (r.outputs || []).map((o) => o && o.name).filter(Boolean);
    const made = new Set(), handMade = new Set(), producers = new Map();
    for (const r of real) for (const n of outs(r)) {
        made.add(n);
        if (!r.station) handMade.add(n);
        (producers.get(n) || producers.set(n, []).get(n)).push(r.station || null);
    }
    const handOnly = real.filter((r) => !r.station);
    const exempt = new Set();
    for (const r of real) {
        if (!r.station) continue;
        const kube = stationKubeFor ? stationKubeFor(r.station) : null;
        if (!kube) continue;
        const build = real.find((b) => !b.station && outs(b).indexOf(kube) >= 0);
        if (!build) continue;
        // What building THIS station costs, expanded through hand recipes only — everything you must already hold
        // before any machine exists.
        const seen = new Set(), stack = Object.keys(build.inputs || {});
        while (stack.length) {
            const n = stack.pop();
            if (seen.has(n)) continue;
            seen.add(n);
            for (const h of handOnly) if (outs(h).indexOf(n) >= 0) for (const k in (h.inputs || {})) stack.push(k);
        }
        // Only the LEAVES matter, and only the circular ones: a name you cannot make by hand, whose every producer
        // is this same station. Steel is in the closure but is hand-craftable, so it stays out of the ground.
        // Titanium is made at the smelter and needed for the Chemical Synthesizer — a DIFFERENT station — so it is
        // not circular and stays excluded too.
        for (const n of seen) {
            if (handMade.has(n) || !made.has(n)) continue;
            const ps = producers.get(n) || [];
            if (ps.length && ps.every((st) => st === r.station)) exempt.add(n);
        }
    }
    const out = new Set();
    for (const n of made) if (!exempt.has(n)) out.add(n);
    return out;
}

// Raw materials do not all live in kubes.json. SILICA is products.json with source:"rock" and density 2.80 — which
// is inside the crust vein band already, it was simply invisible to a pool built from kubes alone. It is the other
// half of the bootstrap: Toolbench and the Induction Smelter are both built from it (their costs live in
// recipes.json; the ore-veins test works the smelter's raw cost out from there rather than remembering it).
export function veinCandidates(kubes, products) {
    // The MINERAL and REGOLITH families are a world's own decorative crust — pickCrust draws its pair from them and
    // ordinary mining already yields them by the hundred. Veining them would bury the ores in materials you are
    // standing on, so they are filtered out here, the same way compositionFor's excludeRegolith does for the mantle.
    // That is what lets the crust band reach up to 3.30 and take in Carbon, Silicon, Phosphorus and Halite without
    // dragging in the scenery.
    const decorative = (n) => / Mineral$/.test(n) || / Regolith$/.test(n);
    const out = (kubes || []).filter((k) => k && k.state === 'solid' && k.name && !decorative(k.name));
    for (const p of (products || [])) if (p && p.source === 'rock' && p.name && !decorative(p.name)) out.push(p);
    return out;
}

// The ores a given world puts in a given layer — its SPECIALITY. Seeded per (addr, layer), so two worlds differ and
// one world is consistent forever. Falls back to the widest band that yields anything, so a thin catalogue can never
// leave a layer empty (which would silently restore the unreachable-tree bug this module exists to fix).
// `made` is the set of names that are the OUTPUT of a real recipe — anything the game intends you to manufacture.
// Excluding it is what keeps the ore->smelt->metal loop intact: Bauxite and Titanium Ore are veined because nothing
// makes them, while Aluminum, Titanium and Iron are not, because smelting is how you are meant to get them. Without
// this the Smelter would be pointless, and Brass Alloy, Inconel and the Superconductor would be lying in the ground.
// MEMOISED per (addr, layer). This is called from the RENDER path — _voxMatIdAt runs for every solid cell in the
// world on every chunk build — and the body filters and sorts the whole candidate pool, which is far too much to do
// per cell. The answer depends only on the world and the layer, so it is computed once and kept.
const _oreCache = new Map();
export function veinOresFor(addr, layer, kubes, made) {
    const ck = addr + '|' + layer;
    const hit = _oreCache.get(ck);
    if (hit) return hit;
    const val = _veinOresUncached(addr, layer, kubes, made);
    if (_oreCache.size > 512) _oreCache.clear();          // bounded: a session visits few worlds
    _oreCache.set(ck, val);
    return val;
}
function _veinOresUncached(addr, layer, kubes, made) {
    const band = VEIN_BANDS[layer];
    if (!band || !Array.isArray(kubes)) return [];
    const isMade = (n) => !!(made && (made.has ? made.has(n) : made[n]));
    const inBand = (lo, hi) => kubes.filter((k) => {
        if (!k || k.state !== 'solid' || !k.name) return false;
        if (isMade(k.name)) return false;
        const d = parseFloat(k.density || '0');
        return d >= lo && d <= hi;
    }).sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));   // NAME order: catalogue order is not stable across edits
    let pool = inBand(band.lo, band.hi);
    if (!pool.length) pool = inBand(Math.max(0, band.lo - 0.5), band.hi + 0.5);
    if (!pool.length) return [];
    // SPECIALITY BY ABUNDANCE, NOT BY PRESENCE. Picking a subset per world reads well until you notice what it does
    // to the tree: outercore held four metals and picking three meant a quarter of all worlds had no Gold — and Gold
    // gates Circuit Board, which gates every station above the bench. A world would simply have been
    // uncompletable, with nothing on screen to say why. So every ore in the band is always present and the SEED sets
    // how much of each there is. A world is "a copper world" because copper is everywhere on it, which is a reason to
    // trade; it is never "the world without gold", which is only a dead end.
    const rng = makeRng(addr + '#vein#' + layer);
    return pool.map((k) => ({ kube: k, weight: 0.15 + rng() * rng() * 2.5 }));   // squared draw → most ores ordinary, a few abundant
}

// Is this world a rich one? One draw per world, so it is a property of the place rather than of where you dig.
export function isRichWorld(addr) { return makeRng(addr + '#veinrich')() < RICH_WORLD_CHANCE; }

// THE FIELD. The ore kube at this cell, or null for ordinary rock. One rng per coarse block, so this is O(1) per
// cell with no neighbour scan: a pocket is placed wholly inside its own block by construction.
// NO VEIN EVER TOUCHES OPEN AIR.
//
// Depth was already the value ladder, but only in the LAYER sense: the crust band runs to the planet's outer shell,
// so a shallow ore pocket could surface and be taken without digging at all. Ore you can walk up to is scenery; ore
// you have to cut into the ground for is a decision about where to spend a mine.
//
// AGAINST THE GENERATED TERRAIN, NEVER THE LIVE GRID, and that distinction is the whole correctness of it. Testing
// _voxSolid — which player edits mutate — would mean a cell's IDENTITY depended on what had been dug nearby: cut a
// tunnel and the ore beside it stops being ore, backfill and it returns. Worse, the server re-derives from the seed
// and would never agree. isSolid here is the pure seed function both sides already share (rockyIsSolid), so a cell
// is buried or it is not, forever, and both ends get the same answer.
//
// Six neighbours, not a radius: that is what "touches open air" actually means, and it covers cave roofs, overhangs
// and the floor of a canyon, none of which a depth test can see.
export function buried(isSolid, x, y, z) {
    return !!isSolid(x + 1, y, z) && !!isSolid(x - 1, y, z)
        && !!isSolid(x, y + 1, z) && !!isSolid(x, y - 1, z)
        && !!isSolid(x, y, z + 1) && !!isSolid(x, y, z - 1);
}

// `isSolid` is the world's own generated-terrain test, and it is REQUIRED: without it this yields no ore at all
// rather than yielding ore that only one side believes in. A caller that forgets gets an obviously empty world —
// which is a bug you find in a minute — instead of a client drawing crust where the server credits copper.
export function veinAt(addr, R, x, y, z, kubes, made, bands, isSolid) {
    if (typeof isSolid !== 'function') return null;
    const B = bands || layerBands(R);
    const layer = layerAt(B, x, y, z);
    const band = VEIN_BANDS[layer];
    if (!band) return null;
    const BLK = veinBlockFor(R);
    const bx = Math.floor(x / BLK), by = Math.floor(y / BLK), bz = Math.floor(z / BLK);
    const rng = makeRng(addr + '#v#' + bx + '_' + by + '_' + bz);
    const rich = isRichWorld(addr);
    const ores = veinOresFor(addr, layer, kubes, made);
    if (!ores.length) return null;
    if (rng() >= layerRate(B, layer, ores.length) * (rich ? RICH_RATE_MULT : 1)) return null;   // draw 1: does this block host a pocket
    // WHICH ore, UNIFORMLY. Choosing by the world's abundance weights read well and quietly reintroduced the failure
    // it was meant to cure: a weight can land at 0.15 against a pool summing to ~7, so that ore is picked about 2% of
    // the time and on a small world with a couple of dozen pockets it simply never appears. One world generated 699
    // vein kubes with no Silica and no Iron at all — the two things the first machine is made of. Presence is now
    // uniform and the weight decides RICHNESS instead, so a world is still "a copper world" by having bigger copper
    // pockets, and is never "the world without iron".
    const pick = ores[Math.min(ores.length - 1, Math.floor(rng() * ores.length))];      // draw 2
    const w = pick.weight;
    const span0 = band.rMax - band.rMin;
    const bonus = w > 1.6 ? 1 : 0;                                                       // an abundant ore digs deeper pockets
    const rad = Math.min(maxRadiusFor(BLK), band.rMin + Math.floor(rng() * (span0 + 1)) + bonus + (rich ? RICH_RADIUS_BONUS : 0));   // draw 3
    // Draws 4-6: a centre far enough inside the block that the pocket cannot spill into a neighbour — which is what
    // lets a single block's hash answer for every cell in it.
    const span = Math.max(1, BLK - 2 * rad);
    const cx = bx * BLK + rad + Math.floor(rng() * span);
    const cy = by * BLK + rad + Math.floor(rng() * span);
    const cz = bz * BLK + rad + Math.floor(rng() * span);
    const dx = x - cx, dy = y - cy, dz = z - cz;
    if (dx * dx + dy * dy + dz * dz > rad * rad) return null;                            // round pocket, not a cube
    if (!buried(isSolid, x, y, z)) return null;                                          // …and never where it would outcrop
    return pick.kube;
}
